import React, { useState, useEffect } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Grid,
  Pagination,
  Skeleton,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ErrorPage from "../Components/ErrorPage";
import SalePaymentsAdd from "./SalePaymentsAdd";
import SalePaymentsEdit from "./SalePaymentsEdit";
import useFetchObjects from "../../api/useFetchObjects";
import useDelete from "../../api/useDelete";
import { useAuth } from "../../context/AuthContext";
import { useTranslation } from "react-i18next";

import { formatJalaliDateTime } from "../utils/dateFormat";
const SalePaymentsIndex = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;

  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(10);
  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [editData, setEditData] = useState(null);

  const query = new URLSearchParams();
  if (searchTerm) query.append("search", searchTerm);
  query.append("page", page);
  query.append("page_size", rowsPerPage);
  const fetchUrl = `shop/customer-balance-payments/?${query.toString()}`;

  const { data, isLoading, isError, refetch } = useFetchObjects(
    ["customer-balance-payments", searchTerm, page, rowsPerPage],
    fetchUrl,
    token
  );
  const { handleDelete, ConfirmDialog } = useDelete(
    "shop/customer-balance-payments",
    token,
    refetch
  );

  useEffect(() => {
    refetch();
  }, [searchTerm, page, rowsPerPage, refetch]);

  const payments = data?.results ?? data ?? [];
  const totalCount = data?.count ?? payments.length;

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setPage(1);
  };

  if (isError) {
    return <ErrorPage message={t("salePayment.failedToLoad", "Failed to load payments.")} />;
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h4">
          {t("salePayment.paymentList", "Payments (reduces customer balance)")}
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => setOpenAdd(true)}
        >
          {t("salePayment.recordPayment", "Record payment")}
        </Button>
      </Box>
      <Typography variant="subtitle1" color="textSecondary" mb={2}>
        {t("salePayment.dataLoadedOn")} {formatJalaliDateTime()}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t("salePayment.balanceNote", "Payments are applied to the customer's current balance (opening + sales). Balance is reduced when you record a payment.")}
      </Typography>
      <Box sx={{ mb: 2 }}>
        <TextField
          variant="outlined"
          fullWidth
          placeholder={t("salePayment.searchPayments", "Search payments...")}
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>
      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table aria-label="payment table">
          <TableHead>
            <TableRow sx={{ backgroundColor: "primary.main" }}>
              <TableCell sx={{ color: "white" }}>#</TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("salePayment.customer", "Customer")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("salePayment.currency", "Currency")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("salePayment.amount", "Amount")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("salePayment.account", "Account")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("salePayment.exchangeRate", "Exchange Rate")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("salePayment.convertedAmount", "Converted Amount")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("salePayment.paymentDate", "Payment date")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("salePayment.notes", "Notes")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("salePayment.action", "Action")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading
              ? Array.from(new Array(5)).map((_, i) => (
                  <TableRow key={`skeleton-${i}`}>
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((j) => (
                      <TableCell key={j}>
                        <Skeleton variant="text" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              : payments.map((payment, i) => {
                  const index = (page - 1) * rowsPerPage + i + 1;
                  return (
                    <TableRow key={payment.id} hover>
                      <TableCell>{index}</TableCell>
                      <TableCell>
                        {payment?.customer_details?.name ?? "—"}
                      </TableCell>
                      <TableCell>
                        {payment?.currency_details?.title ?? "—"}
                      </TableCell>
                      <TableCell>{payment.amount}</TableCell>
                      <TableCell>
                        {payment?.account_details?.name ?? "-"}
                      </TableCell>
                      <TableCell>{payment.exchange_rate ?? "-"}</TableCell>
                      <TableCell>{payment.converted_amount ?? "-"}</TableCell>
                      <TableCell>
                        {payment.payment_date
                          ? formatJalaliDateTime(payment.payment_date)
                          : "—"}
                      </TableCell>
                      <TableCell>{payment.notes || "—"}</TableCell>
                      <TableCell>
                        <Button
                          variant="outlined"
                          size="small"
                          sx={{ mr: 1 }}
                          onClick={() => {
                            setEditData(payment);
                            setOpenEdit(true);
                          }}
                        >
                          {t("salePayment.edit", "Edit")}
                        </Button>
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          onClick={() => handleDelete(payment.id)}
                        >
                          {t("salePayment.delete", "Delete")}
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
          </TableBody>
        </Table>
      </TableContainer>
      <Grid container justifyContent="center" alignItems="center" sx={{ p: 2 }}>
        <Pagination
          count={Math.ceil(totalCount / rowsPerPage) || 1}
          page={page}
          onChange={(e, newPage) => setPage(newPage)}
          variant="outlined"
          color="primary"
        />
      </Grid>
      <ConfirmDialog />
      {openAdd && (
        <SalePaymentsAdd
          open={openAdd}
          onClose={() => setOpenAdd(false)}
          token={token}
          onSuccess={refetch}
        />
      )}
      {openEdit && editData && (
        <SalePaymentsEdit
          open={openEdit}
          onClose={() => {
            setOpenEdit(false);
            setEditData(null);
          }}
          editData={editData}
          token={token}
          onSuccess={refetch}
        />
      )}
    </Box>
  );
};

export default SalePaymentsIndex;
